import type {
  DatadogRum,
  RumEvent,
  RumInitConfiguration,
} from '@datadog/browser-rum';
import {
  redactClientErrorString,
  reportClientError,
  sanitizeClientErrorUrl,
  setClientErrorReporter,
} from './reportClientError';

interface ClientMonitoringConfig {
  applicationId: string;
  clientToken: string;
  site: string;
  service: string;
  env: string;
  version: string;
  sessionSampleRate: number;
  sessionReplaySampleRate: number;
  excludedActivityUrls: NonNullable<
    RumInitConfiguration['excludedActivityUrls']
  >;
}

type MutableRumEvent = RumEvent & {
  action?: {
    target?: {
      name?: string;
    };
  };
  error?: {
    message?: string;
    source?: string;
    stack?: string;
  };
  resource?: {
    url?: string;
  };
  view?: {
    referrer?: string;
    url?: string;
  };
};

const DEFAULT_DATADOG_SITE = 'datadoghq.com';
const DEFAULT_DATADOG_SERVICE = 'descope-flows';
const DEFAULT_SESSION_SAMPLE_RATE = 100;
const DEFAULT_SESSION_REPLAY_SAMPLE_RATE = 0;
const DEFAULT_EXCLUDED_ACTIVITY_URLS: NonNullable<
  RumInitConfiguration['excludedActivityUrls']
> = [
  /^https:\/\/api2\.amplitude\.com\/2\/httpapi(?:[?#]|$)/,
  /^https:\/\/featureassets\.org\/v1\/initialize(?:[?#]|$)/,
  /^https:\/\/www\.googletagmanager\.com\/gtm\.js(?:[?#]|$)/,
];
const STATSIG_NETWORK_ERROR_PATTERN = /\[Statsig\].*networking error occurred/i;
const GENERIC_CROSS_ORIGIN_SCRIPT_ERROR_MESSAGES = new Set([
  'Script error.',
  'Uncaught "Script error."',
  "Uncaught 'Script error.'",
  'Uncaught browser error Script error.',
]);
const WEBKIT_MESSAGE_HANDLERS_ERROR_PATTERN =
  /window\.webkit\.messageHandlers/i;
const CIRCULAR_REACT_DOM_NODE_ERROR_PATTERN =
  /Converting circular structure to JSON[\s\S]*starting at object with constructor 'HTML[A-Za-z]*Element'[\s\S]*__reactFiber\$[\s\S]*stateNode' closes the circle/i;
const ANONYMOUS_APPEND_CHILD_JSON_STACK_PATTERN =
  /at JSON\.stringify @ <anonymous>[\s\S]*at HTML[A-Za-z]*Element\.appendChild @ <anonymous>:\d+:\d+/i;
const EXPECTED_DESCOPE_ERROR_PATTERNS = [
  /Failed to verify email code/i,
  /Let's double-check your (email address|details|password|username)/i,
  /One time code is invalid/i,
  /Password signin failed Wrong password or unknown user/i,
  /That username is already taken/i,
];

let hasInitializedClientMonitoring = false;
let removeGlobalErrorHandlers: (() => void) | undefined;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getStringField = (
  value: Record<string, unknown>,
  key: string
): string | undefined =>
  typeof value[key] === 'string' ? (value[key] as string) : undefined;

const getOptionalEnv = (key: keyof ImportMetaEnv): string | undefined => {
  const value = import.meta.env[key];
  return value === '' ? undefined : value;
};

const getConfiguredActivityUrlExclusions = (): NonNullable<
  RumInitConfiguration['excludedActivityUrls']
> =>
  [
    getOptionalEnv('VITE_BROWSER_FINGERPRINTING_JS_URL'),
    getOptionalEnv('VITE_CEP_JS_URL'),
  ].reduce<NonNullable<RumInitConfiguration['excludedActivityUrls']>>(
    (excludedActivityUrls, url) =>
      url ? [...excludedActivityUrls, url] : excludedActivityUrls,
    DEFAULT_EXCLUDED_ACTIVITY_URLS
  );

const parseSampleRate = (
  value: string | undefined,
  fallback: number
): number => {
  if (value === undefined) {
    return fallback;
  }

  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) {
    return fallback;
  }

  return Math.min(100, Math.max(0, parsedValue));
};

const isAutomaticErrorEvent = (source: string | undefined): boolean =>
  source === 'console' || source === 'source';

const isExpectedDescopeValidationError = (message: string): boolean =>
  message.includes('[Descope]') &&
  EXPECTED_DESCOPE_ERROR_PATTERNS.some(pattern => pattern.test(message));

const getErrorValueText = (
  value: unknown
): { message: string; stack: string } => {
  if (value instanceof Error) {
    return {
      message: value.message,
      stack: value.stack ?? '',
    };
  }

  if (typeof value === 'string') {
    return { message: value, stack: '' };
  }

  if (isRecord(value)) {
    return {
      message:
        getStringField(value, 'message') ??
        getStringField(value, 'error') ??
        getStringField(value, 'detail') ??
        '',
      stack: getStringField(value, 'stack') ?? '',
    };
  }

  return { message: '', stack: '' };
};

const isInjectedWebKitBridgeError = (message: string, stack = ''): boolean => {
  // Instagram/Facebook iOS in-app browsers inject a native bridge script on
  // pagehide. When the WebKit handler is missing it throws from the document
  // URL, even though the script is not served by this app.
  return (
    WEBKIT_MESSAGE_HANDLERS_ERROR_PATTERN.test(message) ||
    WEBKIT_MESSAGE_HANDLERS_ERROR_PATTERN.test(stack)
  );
};

const isInjectedWebKitBridgeErrorValue = (
  value: unknown,
  fallbackMessage = ''
): boolean => {
  const { message, stack } = getErrorValueText(value);
  return isInjectedWebKitBridgeError(message || fallbackMessage, stack);
};

const isInjectedAppendChildCircularJsonError = (
  message: string,
  stack = ''
): boolean => {
  return (
    CIRCULAR_REACT_DOM_NODE_ERROR_PATTERN.test(message) &&
    ANONYMOUS_APPEND_CHILD_JSON_STACK_PATTERN.test(stack)
  );
};

const isInjectedAppendChildCircularJsonErrorValue = (
  value: unknown,
  fallbackMessage = ''
): boolean => {
  const { message, stack } = getErrorValueText(value);
  return isInjectedAppendChildCircularJsonError(
    message || fallbackMessage,
    stack
  );
};

const isGenericCrossOriginScriptError = (
  message: string,
  stack = ''
): boolean => {
  const normalizedMessage = message.trim();

  if (!GENERIC_CROSS_ORIGIN_SCRIPT_ERROR_MESSAGES.has(normalizedMessage)) {
    return false;
  }

  if (normalizedMessage === 'Uncaught browser error Script error.') {
    return true;
  }

  const normalizedStack = stack.trim();

  return (
    normalizedStack.length === 0 ||
    normalizedStack === 'Error: Script error.' ||
    /(?:^|\n)\s*at undefined @\s*$/i.test(normalizedStack)
  );
};

const isGenericCrossOriginScriptErrorValue = (
  value: unknown,
  fallbackMessage = ''
): boolean => {
  const { message, stack } = getErrorValueText(value);
  return isGenericCrossOriginScriptError(message || fallbackMessage, stack);
};

const shouldDropRumEvent = (event: MutableRumEvent): boolean => {
  const message = event.error?.message ?? '';
  const stack = event.error?.stack ?? '';
  const source = event.error?.source;

  if (
    isInjectedWebKitBridgeError(message, stack) ||
    isInjectedAppendChildCircularJsonError(message, stack) ||
    isGenericCrossOriginScriptError(message, stack)
  ) {
    return true;
  }

  if (!isAutomaticErrorEvent(source)) {
    return false;
  }

  return (
    STATSIG_NETWORK_ERROR_PATTERN.test(message) ||
    isExpectedDescopeValidationError(message)
  );
};

export const sanitizeRumEvent = (event: RumEvent): boolean => {
  const mutableEvent = event as MutableRumEvent;

  if (shouldDropRumEvent(mutableEvent)) {
    return false;
  }

  if (mutableEvent.view?.url) {
    mutableEvent.view.url = sanitizeClientErrorUrl(mutableEvent.view.url);
  }

  if (mutableEvent.view?.referrer) {
    mutableEvent.view.referrer = sanitizeClientErrorUrl(
      mutableEvent.view.referrer
    );
  }

  if (mutableEvent.resource?.url) {
    mutableEvent.resource.url = sanitizeClientErrorUrl(
      mutableEvent.resource.url
    );
  }

  if (mutableEvent.error?.message) {
    mutableEvent.error.message = redactClientErrorString(
      mutableEvent.error.message
    );
  }

  if (mutableEvent.error?.stack) {
    mutableEvent.error.stack = redactClientErrorString(
      mutableEvent.error.stack
    );
  }

  if (mutableEvent.action?.target?.name) {
    mutableEvent.action.target.name = '[REDACTED_ACTION]';
  }

  return true;
};

export const getClientMonitoringConfig = ():
  ClientMonitoringConfig | undefined => {
  if (getOptionalEnv('VITE_DATADOG_RUM_ENABLED') === 'false') {
    return undefined;
  }

  const applicationId = getOptionalEnv('VITE_DATADOG_RUM_APPLICATION_ID');
  const clientToken = getOptionalEnv('VITE_DATADOG_CLIENT_TOKEN');

  if (!applicationId || !clientToken) {
    return undefined;
  }

  if (
    import.meta.env.DEV &&
    getOptionalEnv('VITE_DATADOG_RUM_ENABLED') !== 'true'
  ) {
    return undefined;
  }

  return {
    applicationId,
    clientToken,
    site: getOptionalEnv('VITE_DATADOG_SITE') ?? DEFAULT_DATADOG_SITE,
    service: getOptionalEnv('VITE_DATADOG_SERVICE') ?? DEFAULT_DATADOG_SERVICE,
    env:
      getOptionalEnv('VITE_DATADOG_ENV') ??
      getOptionalEnv('VITE_STATSIG_STAGE') ??
      import.meta.env.MODE,
    version: getOptionalEnv('VITE_BUILD_VERSION') ?? 'local',
    sessionSampleRate: parseSampleRate(
      getOptionalEnv('VITE_DATADOG_RUM_SESSION_SAMPLE_RATE'),
      DEFAULT_SESSION_SAMPLE_RATE
    ),
    sessionReplaySampleRate: parseSampleRate(
      getOptionalEnv('VITE_DATADOG_RUM_REPLAY_SAMPLE_RATE'),
      DEFAULT_SESSION_REPLAY_SAMPLE_RATE
    ),
    excludedActivityUrls: getConfiguredActivityUrlExclusions(),
  };
};

export const initializeClientMonitoring = async (): Promise<boolean> => {
  if (hasInitializedClientMonitoring) {
    return true;
  }

  const config = getClientMonitoringConfig();
  if (!config) {
    return false;
  }

  try {
    const { datadogRum } = await import('@datadog/browser-rum');
    const initConfig: RumInitConfiguration = {
      applicationId: config.applicationId,
      clientToken: config.clientToken,
      site: config.site,
      service: config.service,
      env: config.env,
      version: config.version,
      sessionSampleRate: config.sessionSampleRate,
      sessionReplaySampleRate: config.sessionReplaySampleRate,
      trackUserInteractions: false,
      trackResources: true,
      trackLongTasks: true,
      excludedActivityUrls: config.excludedActivityUrls,
      defaultPrivacyLevel: 'mask-user-input',
      beforeSend: sanitizeRumEvent,
      silentMultipleInit: true,
    };

    datadogRum.init(initConfig);
    setClientErrorReporter(datadogRum as Pick<DatadogRum, 'addError'>);

    if (config.sessionReplaySampleRate > 0) {
      datadogRum.startSessionReplayRecording();
    }

    hasInitializedClientMonitoring = true;
    return true;
  } catch (error) {
    reportClientError('Datadog RUM failed to initialise', error, {
      source: 'clientMonitoring',
    });
    return false;
  }
};

export const registerGlobalClientErrorHandlers = (): (() => void) => {
  if (removeGlobalErrorHandlers) {
    return removeGlobalErrorHandlers;
  }

  const handleError = (event: ErrorEvent) => {
    if (
      isInjectedWebKitBridgeErrorValue(event.error, event.message) ||
      isInjectedAppendChildCircularJsonErrorValue(event.error, event.message) ||
      isGenericCrossOriginScriptErrorValue(event.error, event.message)
    ) {
      return;
    }

    reportClientError('Uncaught browser error', event.error ?? event.message, {
      source: 'window.onerror',
    });
  };

  const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    reportClientError('Unhandled promise rejection', event.reason, {
      source: 'unhandledrejection',
    });
  };

  window.addEventListener('error', handleError);
  window.addEventListener('unhandledrejection', handleUnhandledRejection);

  removeGlobalErrorHandlers = () => {
    window.removeEventListener('error', handleError);
    window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    removeGlobalErrorHandlers = undefined;
  };

  return removeGlobalErrorHandlers;
};

export const resetClientMonitoringForTests = (): void => {
  hasInitializedClientMonitoring = false;
  removeGlobalErrorHandlers?.();
};
